import type { QueryClient } from "@tanstack/react-query";
import { create, type StoreApi, type UseBoundStore } from "zustand";

import { ApiError } from "../../api/client";
import { k } from "../../i18n/i18n";

/** Rédaction diffusée d'un document du dossier d'exercice (mise en ambiance, fiche pays) : étapes, textes qui
 *  s'écrivent, sections terminées. Tenue hors de la vue : l'IA continue d'écrire quand on passe sur la carte ou
 *  dans les Données initiales, et la barre latérale l'indique. Une rédaction à la fois par document. */

/** Étape de la rédaction : `pending` à venir (annoncée d'emblée par le serveur), `running`, `done` ; `fallback` :
 *  section faite sans le texte de l'IA (montrée faite, comme `done`) ; `error` : lecture du dossier impossible. */
export interface JobStep {
  id: string;
  label: string;
  state: "pending" | "running" | "done" | "error" | "fallback";
}

export interface DocumentJob<S> {
  exerciseId: string;
  running: boolean;
  steps: JobStep[];
  /** Texte en cours d'écriture par section ; `draft` : premier jet français d'un dossier anglais. */
  live: Record<string, { text: string; draft: boolean }>;
  /** Sections terminées pendant la rédaction (avant le document complet). */
  sections: Record<string, Partial<S>>;
  source: string | null;
  /** Message d'erreur tel que reçu (serveur, ou clé du client) : traduit à l'affichage (`tServer`). */
  error: string | null;
  startedAt: number;
  elapsed: number | null;
  stopped: boolean;
  /** Sections demandées ; null : tout le document. */
  targets: string[] | null;
  controller: AbortController;
}

/** Corps d'une demande de rédaction : sections demandées (null : toutes), reprise d'une rédaction interrompue. */
export interface JobBody {
  sections?: string[] | null;
  resume?: boolean;
}

/** Événement diffusé par le serveur (step, token, section, source, done, error, et ceux propres au document). */
export interface JobEvent {
  event: string;
  data: {
    id?: string;
    label?: string;
    state?: JobStep["state"];
    text?: string;
    draft?: boolean;
    reset?: boolean;
    message?: string;
    elapsed_s?: number;
  };
}

export interface DocumentJobStore<S, B extends JobBody> {
  job: DocumentJob<S> | null;
  start: (exerciseId: string, body: B, queryClient: QueryClient) => Promise<void>;
  stop: () => void;
  dismiss: () => void;
}

/** Magasin de la rédaction d'un document : `stream` lit le flux du serveur, `stopRemote` lui dit l'arrêt voulu,
 *  `document` rend le document entier qu'un événement porte (`done`, ou la fiche composée de la fiche pays), posé
 *  aussitôt dans le cache de la vue (`queryKey`). */
export function createDocumentJob<D extends { sections: { id: string }[] }, S, B extends JobBody>(options: {
  queryKey: (exerciseId: string | null) => readonly unknown[];
  stream: (exerciseId: string, body: B, onEvent: (e: JobEvent) => void, signal: AbortSignal) => Promise<void>;
  stopRemote: (exerciseId: string) => Promise<unknown>;
  document: (event: string, data: Record<string, unknown>) => D | null | undefined;
}): UseBoundStore<StoreApi<DocumentJobStore<S, B>>> {
  return create<DocumentJobStore<S, B>>((set, get) => ({
    job: null,
    start: async (exerciseId, body, queryClient) => {
      if (get().job?.running) return;
      const controller = new AbortController();
      set({
        job: {
          exerciseId,
          running: true,
          steps: [],
          live: {},
          sections: {},
          source: null,
          error: null,
          startedAt: performance.now(),
          elapsed: null,
          stopped: false,
          targets: body.sections ?? null,
          controller,
        },
      });
      const update = (fn: (job: DocumentJob<S>) => Partial<DocumentJob<S>>) => {
        const job = get().job;
        if (job && job.controller === controller) set({ job: { ...job, ...fn(job) } });
      };
      const onEvent = ({ event, data }: JobEvent) => {
        const document = options.document(event, data as Record<string, unknown>);
        if (document) queryClient.setQueryData(options.queryKey(exerciseId), { ...document, unavailable: null });
        if (event === "step" && data.id) {
          const step: JobStep = { id: data.id, label: data.label ?? data.id, state: data.state ?? "running" };
          update((j) => ({
            steps: j.steps.some((s) => s.id === step.id)
              ? j.steps.map((s) => (s.id === step.id ? step : s))
              : [...j.steps, step],
          }));
        } else if (event === "source") {
          update(() => ({ source: data.label ?? null }));
        } else if (event === "token" && data.id) {
          const id = data.id;
          update((j) => {
            const current = data.reset ? { text: "", draft: false } : (j.live[id] ?? { text: "", draft: false });
            return {
              live: { ...j.live, [id]: { text: current.text + (data.text ?? ""), draft: Boolean(data.draft) } },
            };
          });
        } else if (event === "section" && data.id) {
          const id = data.id;
          const section = data as Partial<S>;
          update((j) => {
            const live = { ...j.live };
            delete live[id];
            return { live, sections: { ...j.sections, [id]: section } };
          });
          // Document déjà affiché : la section terminée y prend place tout de suite.
          queryClient.setQueryData<D>(options.queryKey(exerciseId), (prev) =>
            prev
              ? { ...prev, sections: prev.sections.map((s) => (s.id === id ? { ...s, ...section } : s)) }
              : prev,
          );
        } else if (event === "error") {
          update(() => ({ error: data.message ?? k("Rédaction impossible.") }));
        } else if (event === "done") {
          update(() => ({ elapsed: data.elapsed_s ?? null }));
        }
      };
      try {
        await options.stream(exerciseId, body, onEvent, controller.signal);
      } catch (err) {
        if (controller.signal.aborted) update(() => ({ stopped: true }));
        else
          update(() => ({
            error: err instanceof ApiError ? err.message : k("Rédaction interrompue : le serveur ne répond pas."),
          }));
      } finally {
        update(() => ({ running: false, live: {} }));
        // Reprise d'une rédaction interrompue sans rien à rédiger (reprise déjà lancée par un autre poste, sections
        // remplies entre-temps) : aucun bandeau.
        const job = get().job;
        if (body.resume && job?.controller === controller && !job.error && !job.stopped && job.steps.length === 0)
          set({ job: null });
        void queryClient.invalidateQueries({ queryKey: options.queryKey(exerciseId) });
      }
    },
    // Arrêt voulu : le serveur le retient, pour que ni ce poste ni un autre ne reprenne d'office les sections restées
    // en attente ; la vue ne relance pas non plus la reprise tant que ce bandeau « arrêtée » est là.
    stop: () => {
      const job = get().job;
      if (!job?.running) return;
      job.controller.abort();
      void options.stopRemote(job.exerciseId).catch(() => undefined);
    },
    dismiss: () => {
      const job = get().job;
      if (job && !job.running) set({ job: null });
    },
  }));
}

/** Clé de la reprise d'office d'une rédaction interrompue ; null : rien à reprendre. Jamais après un arrêt voulu
 *  sur ce poste (`stopped`, bandeau « arrêtée » affiché) ni pendant une rédaction ; le serveur, lui, ne signale
 *  plus de sections interrompues après un arrêt voulu sur n'importe quel poste. */
export function resumeKeyFor(
  exerciseId: string | null,
  document: { version: number; language_mismatch?: boolean; interrupted?: string[] } | undefined,
  state: { complete: boolean; running: boolean; stopped: boolean },
): string | null {
  const interrupted = document?.interrupted ?? [];
  if (!exerciseId || !state.complete || state.running || state.stopped) return null;
  if (!document || document.language_mismatch || interrupted.length === 0) return null;
  return `${exerciseId}:${document.version}:${interrupted.join(",")}`;
}

/** Rédaction en cours pour le voile des feuilles (GenerationVeil) : celle de ce poste tant qu'elle tourne,
 *  sinon celle que le serveur signale (autre poste, ou rédaction d'avant un rechargement). Arrêtée, échouée
 *  ou finie sur ce poste : plus de voile, même si le document relu la dit encore en cours un instant. */
export function writingInProgress(
  job: { running: boolean } | null | undefined,
  writing: boolean | undefined,
): boolean {
  return job ? job.running : Boolean(writing);
}
